// @flow
import React from 'react';
import { constants } from '@clubmed/utils';

import { addTranslationKeys } from 'Src/i18n';
import { getAdultsOptions, getChildrenOptions } from 'Store/utils/selectOptionsBuilders';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { getNewsletterOptins } from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import {
  type FormProduct,
  type FormStatus,
} from 'Components/widgets/product-quotation-form/productQuotationForm.utils';
import {
  QUOTATION_FORM_SECTION_ID,
  MAX_FORM_ADULTS,
  MAX_FORM_KIDS,
  MAX_FORM_TEENS,
  MIN_FORM_ADULTS,
  MIN_FORM_KIDS,
  MIN_FORM_TEENS,
  NO_VALUE,
  RANGE_ESCAPADE,
} from 'Components/widgets/product-quotation-form/productQuotationForm.constants';
import { ProductQuotationFormContainer } from 'Components/widgets/product-quotation-form/ProductQuotationFormContainer';
import { BE } from 'Tracking/constants';
import { momentFromApiDate } from 'Utils/date/apiDate';

const { PRODUCT_TYPES } = constants;

const SUMMER_CAMP_AVAILABLE_DATES = [
  { value: '20210626' },
  { value: '20210703' },
  { value: '20210710' },
];

export type ProductQuotationFormLineProps = {|
  coordinates: {
    civility: string,
    email: string,
    firstName: string,
    informationLabel: string,
    lastName: string,
    phoneNumber: string,
    title: string,
  },
  criteria: {
    dateFormat: string,
    dateLabel: string,
    datePlaceholder: string,
    departureDateEnd: string,
    departureDateStart: string,
    extensionLabel: string,
    extensionPlaceholder: string,
    noExtension: string,
    noTransportation: string,
    title: string,
    transportationLabel: string,
    transportationPlaceholder: string,
  },
  pax: {
    adultAgeIndication: string,
    adultsChoices: PluralizedLabel,
    kidsAgeIndicationEscapade: string,
    kidsAgeIndicationSummerCamp: string,
    kidsAgeIndicationTour: string,
    kidsChoices: PluralizedLabel,
    noMoreAvailableDates: string,
    paxLabel: string,
    teensChoices: PluralizedLabel,
    title: string,
  },
  quotation: {
    buttonLabel: string,
    error: FormStatus,
    indication: Array<string>,
    sending: string,
    success: FormStatus,
  },
  resortId: string,
  specialRequest: {
    placeholder: string,
    title: string,
  },
  title: string,
|};

// Resolver props

export type LegalInformation = {
  id: string,
  title: string,
  description: string,
};

export type FormFieldOption = {
  name: string,
  value: string,
};

export type ProductQuotationFormResolverProps = {
  bookingCriteria: { departureCities: $ReadOnlyArray<FormFieldOption> },
  civilities: { adult: $ReadOnlyArray<FormFieldOption> },
  legalInformations: { newsletterOptins: Array<LegalInformation> },
  newsletter: { id: string, description: string },
  price?: { initialPrice: null | { value: number } },
  product: FormProduct,
  stopover?: { tour: { duration: string }, extension: { duration: string, title: string } },
  tour: { availableDates: $ReadOnlyArray<FormFieldOption> },
};

type ProductQuotationFormProps = {
  ...$Exact<ProductQuotationFormLineProps>,
  ...$Exact<ProductQuotationFormResolverProps>,
};

export function ProductQuotationFormLine(props: ProductQuotationFormProps) {
  const {
    bookingCriteria,
    civilities,
    coordinates,
    criteria,
    legalInformations,
    newsletter,
    pax,
    price,
    product,
    quotation,
    specialRequest,
    stopover,
    title,
    tour,
  } = props;

  addTranslationKeys({
    productQuotationForm: {
      coordinates,
      criteria,
      pax,
      quotation,
      specialRequest,
      title,
    },
  });

  const { type: productType } = product;

  const isEscapade = product.rangeId === RANGE_ESCAPADE;
  const isSummerCamp = productType === PRODUCT_TYPES.SUMMER_CAMP;

  let tourDuration = parseInt(stopover?.tour.duration || '0', 10);
  const extensionName = stopover?.extension.title || '';
  const extensionOptions = [];
  // The API returns an empty string when no extension is available
  if (extensionName.length > 0) {
    extensionOptions.push(
      { value: NO_VALUE, name: criteria.noExtension },
      { value: extensionName, name: extensionName },
    );

    tourDuration += parseInt(stopover?.extension.duration || '0', 10);
  }

  let departureCityOptions = bookingCriteria.departureCities;
  // The API returns this array when no transport is available:
  // [{value : "NO", name : "No Transport"}]
  if (departureCityOptions.length === 1 && departureCityOptions[0].value === NO_VALUE) {
    departureCityOptions = [];
  }

  const availableDatesIndication = pax.noMoreAvailableDates;
  const ageIndicationAdults = isSummerCamp
    ? pax.kidsAgeIndicationSummerCamp
    : pax.adultAgeIndication;
  const ageIndicationKids = isEscapade ? pax.kidsAgeIndicationEscapade : pax.kidsAgeIndicationTour;

  const departureDateStart = new Date(criteria.departureDateStart);
  const departureDateEnd = new Date(criteria.departureDateEnd);
  const adultsOptions = isSummerCamp
    ? getChildrenOptions(pax.teensChoices, MIN_FORM_TEENS, MAX_FORM_TEENS).map(
        ({ label, value }) => ({
          name: label,
          value: value.toString(),
        }),
      )
    : getAdultsOptions(pax.adultsChoices, MIN_FORM_ADULTS, MAX_FORM_ADULTS).map(
        ({ label, value }) => ({
          name: label,
          value: value.toString(),
        }),
      );
  const kidsOptions = getChildrenOptions(pax.kidsChoices, MIN_FORM_KIDS, MAX_FORM_KIDS).map(
    ({ label, value }) => ({
      name: label,
      value: value.toString(),
    }),
  );

  const newsletterOptins = getNewsletterOptins(legalInformations.newsletterOptins || []);
  const trackingData = {
    actionGA: 'accommodation',
    categoryGA: 'bookingEngineEntry',
    labelGA: 'accommodation',
    basePriceAccommodation: price?.initialPrice?.value || null,
    resortCode: product.productId,
    resortDefaultPrice: price?.initialPrice?.value || null,
    resortNightNumber: tourDuration,
    resortSeasonType: product.seasonName,
    resortUrlImage: product.image,
    siteSection: BE,
  };

  return (
    <SiteContainer
      id={QUOTATION_FORM_SECTION_ID}
      dataTestId={QUOTATION_FORM_SECTION_ID}
      className="Line"
    >
      <div className="PriceTicket-sticky-beacon" />
      <ProductQuotationFormContainer
        adultsOptions={adultsOptions}
        ageIndicationAdults={ageIndicationAdults}
        ageIndicationKids={ageIndicationKids}
        availableDatesIndication={availableDatesIndication}
        civilityOptions={civilities?.adult || []}
        dateFormat={criteria.dateFormat}
        departureCityOptions={departureCityOptions}
        departureDateEnd={departureDateEnd}
        departureDateOptions={
          // TODO: this is done for ODR purpose to do remove this when
          // the summer camp date are set
          isSummerCamp
            ? SUMMER_CAMP_AVAILABLE_DATES.map(({ value }) => ({
                name: momentFromApiDate(value).format(criteria.dateFormat),
                value,
              }))
            : tour.availableDates.map(({ name, value }) => ({
                name,
                value: value.toString(),
              }))
        }
        departureDateStart={departureDateStart}
        extensionOptions={extensionOptions}
        footnotes={quotation.indication}
        isEscapade={isEscapade}
        isSummerCamp={isSummerCamp}
        kidsOptions={kidsOptions}
        newsletter={newsletter}
        newsletterOptins={newsletterOptins}
        product={product}
        quotation={{ success: quotation.success, error: quotation.error }}
        trackingData={trackingData}
      />
    </SiteContainer>
  );
}
